import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import WizardLayout from './WizardLayout';
import WelcomeStep from './steps/WelcomeStep';
import UserInfoStep from './steps/UserInfoStep';
import ProjectInfoStep from './steps/ProjectInfoStep';
import FloorsStep from './steps/FloorsStep';
import FloorPlanStep from './steps/FloorPlanStep';
import SpacesStep from './steps/SpacesStep';
import HomeChoicesStep from './steps/HomeChoicesStep';
import SummaryStep from './steps/SummaryStep';
import { useData } from '../../../hooks/useData';
import { TABLES } from '../../../constants';
import { useApp } from '../../../contexts/AppContext';
import { useErrorLogging, ERROR_SEVERITY, ERROR_CATEGORY } from '../../../utils/errorLogging';
import { validateRequired, validatePhone } from '../../../utils/validation';
import {
  autoCreateStaircaseSpace,
  autoCreateConstructionItems,
} from '../../../utils/autoProjectItems';

const OnboardingWizard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isNewProject = searchParams.get('new') === 'true';
  const session = useSession();
  const supabase = useSupabaseClient();
  const { currentProject, setCurrentProject, addAndSelectProject } = useApp();
  const { logError } = useErrorLogging();
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [projectId, setProjectId] = useState(null); // Track created project ID
  const [savedFloorIds, setSavedFloorIds] = useState({}); // Map local floor IDs to DB floor IDs
  const [isInitializing, setIsInitializing] = useState(true); // Loading existing data

  // Fetch space types for SpacesStep
  const { data: spaceTypes } = useData(TABLES.MASTER_SPACES);

  // Get data hooks for mutations
  const { update: updateUserProfile } = useData(TABLES.USER_PROFILES);
  const { manageProject } = useData(TABLES.PROJECT_INFO);

  // Fetch home choice items for HomeChoicesStep (both whole_home and floor_specific)
  const { data: homeChoiceItems } = useData(TABLES.REFERENCE_HOME_CHOICE_ITEMS, {
    select: `
      *,
      master_item:master_items(
        id,
        item_name,
        item_options,
        is_bundle,
        bundle_type,
        bundle_items
      )
    `,
  });

  // Collect choice bundle child IDs for fetching names
  const choiceBundleChildIds = useMemo(() => {
    if (!homeChoiceItems) return [];
    const ids = [];
    homeChoiceItems.forEach(item => {
      if (item.master_item?.bundle_type === 'choice' && item.master_item?.bundle_items?.items) {
        item.master_item.bundle_items.items.forEach(child => ids.push(child.item_id));
      }
    });
    return [...new Set(ids)];
  }, [homeChoiceItems]);

  const { data: bundleChildItems } = useData(TABLES.MASTER_ITEMS, {
    select: 'id, item_name, estimated_cost',
    filter: { id: choiceBundleChildIds },
    enabled: choiceBundleChildIds.length > 0,
    realtime: false,
  });

  // Store data for each step
  const [wizardData, setWizardData] = useState({
    userInfo: {},
    projectInfo: {},
    floors: {},
    floorPlan: {},
    spaces: {},
    homeChoices: {},
    items: {},
  });

  // Store extracted spaces from floor plan analysis (for prefilling SpacesStep)
  const [extractedSpaces, setExtractedSpaces] = useState({});

  // Load existing user/project data on mount (for returning users who didn't complete wizard)
  useEffect(() => {
    const loadExistingData = async () => {
      if (!session?.user) {
        setIsInitializing(false);
        return;
      }

      try {
        // Fetch user profile
        const { data: profile } = await supabase
          .from(TABLES.USER_PROFILES)
          .select('full_name, phone, role_id')
          .eq('id', session.user.id)
          .single();

        // If ?new=true, skip project loading — user wants a fresh wizard
        // Otherwise use currentProject or fall back to first project from join_project_users
        let targetProjectId = null;

        if (!isNewProject) {
          targetProjectId = currentProject?.id;

          if (!targetProjectId) {
            const { data: projectLinks } = await supabase
              .from(TABLES.JOIN_PROJECT_USERS)
              .select('project_id')
              .eq('user_id', session.user.id)
              .limit(1);
            targetProjectId = projectLinks?.[0]?.project_id;
          }
        }

        // Clear the ?new param from the URL so refreshing doesn't re-trigger
        if (isNewProject) {
          setSearchParams({}, { replace: true });
        }

        let existingProject = null;
        if (targetProjectId) {
          const projectId = targetProjectId;

          // Fetch project info, floors, and spaces separately to avoid RLS issues with nested selects
          const { data: projectData } = await supabase
            .from(TABLES.PROJECT_INFO)
            .select('*')
            .eq('id', projectId)
            .single();

          if (projectData) {
            const { data: floorsData } = await supabase
              .from(TABLES.PROJECT_FLOORS)
              .select('*')
              .eq('project_id', projectId);

            const floorIds = floorsData?.map(f => f.id) || [];
            const { data: spacesData } =
              floorIds.length > 0
                ? await supabase.from(TABLES.PROJECT_SPACES).select('*').in('floor_id', floorIds)
                : { data: [] };

            existingProject = {
              ...projectData,
              project_floors: floorsData || [],
              project_spaces: spacesData || [],
            };
          }
        }

        // Pre-populate wizard data
        const newWizardData = { ...wizardData };

        // Step 1: User info
        if (profile) {
          newWizardData.userInfo = {
            fullName: profile.full_name || '',
            phone: profile.phone || '',
            role: profile.role_id || '',
            email: session.user.email || '',
          };
        }

        // Step 2+: Project data
        if (existingProject) {
          setProjectId(existingProject.id);
          // Ensure currentProject is set (in case user came directly to wizard URL)
          if (!currentProject || currentProject.id !== existingProject.id) {
            setCurrentProject(existingProject);
          }

          newWizardData.projectInfo = {
            projectName: existingProject.project_name || '',
            address: existingProject.address || '',
            constructionType: existingProject.construction_type_id || '',
            startDate: existingProject.start_date || '',
            projectPhase: existingProject.project_phase_id || '',
            totalBudget: existingProject.total_budget || '',
          };

          // Step 3: Floors
          if (existingProject.project_floors?.length > 0) {
            const floorIdMap = {};
            const floors = existingProject.project_floors.map(f => {
              const localId = `floor-${f.id}`;
              floorIdMap[localId] = f.id;
              return {
                id: localId,
                floorNum: f.floor_number,
                name: f.custom_name || '',
              };
            });
            newWizardData.floors = { floors };
            setSavedFloorIds(floorIdMap);

            // Step 4: Spaces
            if (existingProject.project_spaces?.length > 0) {
              const spaces = existingProject.project_spaces.map(s => ({
                id: `space-${s.id}`,
                dbId: s.id,
                floor_id: `floor-${s.floor_id}`,
                master_space_id: s.master_space_id,
                custom_space_name: s.custom_space_name || '',
                room_size: s.room_size,
                room_height: s.room_height,
                description: s.description || '',
              }));
              newWizardData.spaces = { spaces };
            }
          }

          // Determine which step to resume from
          // Steps: 1=User, 2=Project, 3=Floors, 4=FloorPlan, 5=Spaces, 6=HomeChoices, 7=Summary
          let resumeStep = 1;
          if (profile?.full_name) resumeStep = 2;
          if (existingProject.address) resumeStep = 3;
          if (existingProject.project_floors?.length > 0) resumeStep = 5; // Skip to Spaces (FloorPlan is optional)
          if (existingProject.project_spaces?.length > 0) resumeStep = 6; // HomeChoices

          setCurrentStep(resumeStep);
        }

        setWizardData(newWizardData);
      } catch (err) {
        console.error('Error loading existing wizard data:', err);
      } finally {
        setIsInitializing(false);
      }
    };

    loadExistingData();
  }, [session?.user?.id, supabase]);

  // Handle step data changes
  const handleUserInfoChange = useCallback(data => {
    setWizardData(prev => ({ ...prev, userInfo: data }));
  }, []);

  const handleProjectInfoChange = useCallback(data => {
    setWizardData(prev => ({ ...prev, projectInfo: data }));
  }, []);

  const handleFloorsChange = useCallback(data => {
    setWizardData(prev => ({ ...prev, floors: data }));
  }, []);

  const handleFloorPlanChange = useCallback(data => {
    setWizardData(prev => ({ ...prev, floorPlan: data }));
  }, []);

  // Handle extracted spaces from floor plan analysis
  const handleExtractedSpaces = useCallback((floorId, spaces) => {
    setExtractedSpaces(prev => ({
      ...prev,
      [floorId]: spaces,
    }));
  }, []);

  const handleSpacesChange = useCallback(data => {
    setWizardData(prev => ({ ...prev, spaces: data }));
  }, []);

  const handleHomeChoicesChange = useCallback(data => {
    setWizardData(prev => ({ ...prev, homeChoices: data }));
  }, []);

  // Handle back navigation
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Save Step 1: Update user profile
  const saveUserInfo = async () => {
    const { fullName, phone, role } = wizardData.userInfo;
    if (!fullName && !phone) return; // Nothing to save

    const updateData = {
      full_name: fullName || null,
      phone: phone || null,
    };

    // Resolve role_id from the user's selection
    // Values are either a direct UUID (Owner/PM) or UUID + suffix (e.g. PM_architect)
    // For suffixed values, extract the UUID prefix which is the actual role_id
    if (role) {
      // UUIDs are 36 chars; if value is longer, it has a suffix — extract the UUID part
      const roleId = role.length > 36 ? role.substring(0, 36) : role;
      updateData.role_id = roleId;
    } else {
      // Fallback: if no role selected, check if user already has one; if not, assign Owner
      const { data: profile } = await supabase
        .from(TABLES.USER_PROFILES)
        .select('role_id')
        .eq('id', session.user.id)
        .single();

      if (!profile?.role_id) {
        const { data: ownerRole } = await supabase
          .from(TABLES.REFERENCE_ROLES)
          .select('id')
          .eq('name', 'Owner')
          .single();
        if (ownerRole) {
          updateData.role_id = ownerRole.id;
        }
      }
    }

    await updateUserProfile({
      id: session.user.id,
      data: updateData,
    });
  };

  // Save Step 2: Create project
  const saveProjectInfo = async () => {
    const { address, constructionTypeId, startDate, projectPhaseId, totalBudget, projectName } =
      wizardData.projectInfo;

    const newProject = await manageProject({
      p_address: address,
      p_construction_type_id: constructionTypeId || null,
      p_project_phase_id: projectPhaseId || null,
      p_total_area: null,
      p_plot_size: null,
      p_num_floors: 0, // Will update after floors step
      p_total_budget: totalBudget ? Number(totalBudget) : null,
      p_start_date: startDate || null,
      p_estimated_completion: null,
      p_user_id: session.user.id,
      p_has_basement: false, // Will update after floors step
      p_floors: null, // We'll add floors in step 3
    });

    // Save project nickname if provided (not part of manage_project RPC)
    if (projectName && newProject.id) {
      await supabase
        .from(TABLES.PROJECT_INFO)
        .update({ project_name: projectName })
        .eq('id', newProject.id);
    }

    setProjectId(newProject.id);
    addAndSelectProject(newProject);
    return newProject;
  };

  // Save Step 3: Save floors and update project num_floors
  const saveFloors = async () => {
    if (!projectId) return;

    const floors = wizardData.floors?.floors || [];

    // Build RPC payload from FloorsStep data
    const floorsPayload = floors.map(floor => ({
      floor_number: floor.floorNum,
      floor_area: floor.floorArea || null,
      floor_height: floor.ceilingHeight || 2.7,
      floor_type: floor.floorType || 'new',
      stairs_to_floor: floor.stairs || null,
      notes: floor.notes || null,
    }));

    // Save floors via RPC
    const { error: floorsError } = await supabase.rpc('manage_project_floors', {
      p_project_id: projectId,
      p_floors: floorsPayload,
    });

    if (floorsError) throw floorsError;

    // Fetch the saved floors to get their DB IDs
    const { data: dbFloors, error: fetchError } = await supabase
      .from(TABLES.PROJECT_FLOORS)
      .select('id, floor_number')
      .eq('project_id', projectId);

    if (fetchError) throw fetchError;

    // Create mapping from local floor IDs to DB floor IDs
    const floorIdMap = {};
    floors.forEach(localFloor => {
      const dbFloor = dbFloors?.find(f => f.floor_number === localFloor.floorNum);
      if (dbFloor) {
        floorIdMap[localFloor.id] = dbFloor.id;
      }
    });
    setSavedFloorIds(floorIdMap);

    // Auto-create גרם מדרגות space + מדרגות לגרם item for floors with stairs
    for (const localFloor of floors) {
      if (localFloor.stairs && localFloor.stairs > 0) {
        const dbFloorId = floorIdMap[localFloor.id];
        if (dbFloorId) {
          try {
            await autoCreateStaircaseSpace(supabase, {
              projectId,
              floorId: dbFloorId,
              stairCount: localFloor.stairs,
            });
          } catch (err) {
            console.error('Failed to auto-create staircase space:', err);
          }
        }
      }
    }

    // Update project with num_floors
    await supabase
      .from(TABLES.PROJECT_INFO)
      .update({ num_floors: floors.length })
      .eq('id', projectId);
  };

  // Save Step 5: Save home choices as project_default_options
  const saveHomeChoices = async () => {
    if (!projectId) return;

    const selections = wizardData.homeChoices?.selections || {};
    if (Object.keys(selections).length === 0) return;

    // Get floor IDs from saved floors
    const { data: savedFloors } = await supabase
      .from(TABLES.PROJECT_FLOORS)
      .select('id, floor_number')
      .eq('project_id', projectId);

    const floorMap = {};
    savedFloors?.forEach(f => {
      floorMap[f.floor_number] = f.id;
    });

    // Build default options records
    const defaultOptions = [];

    for (const [masterItemId, selection] of Object.entries(selections)) {
      // Build selected_option based on whether it's a choice bundle or regular item
      // Use current format: { group: optionName } (not legacy type/value format)
      const selectedOption = selection.selected_child_id
        ? { selected_child_id: selection.selected_child_id }
        : selection.groups || {};

      const baseOption = {
        project_id: projectId,
        master_item_id: masterItemId,
        selected_option: selectedOption,
        created_by: session.user.id,
        updated_by: session.user.id,
      };

      // Handle floor-specific selections
      if (selection.floors) {
        if (selection.floors.allFloors) {
          // NULL floor_id means all floors
          defaultOptions.push({ ...baseOption, floor_id: null });
        } else if (selection.floors.floorIds?.length > 0) {
          // Create one record per selected floor
          for (const floorId of selection.floors.floorIds) {
            defaultOptions.push({ ...baseOption, floor_id: floorId });
          }
        }
      } else {
        // Whole-home item (no floor selection)
        defaultOptions.push({ ...baseOption, floor_id: null });
      }
    }

    // Insert all default options
    if (defaultOptions.length > 0) {
      const { error: insertError } = await supabase
        .from(TABLES.PROJECT_DEFAULT_OPTIONS)
        .insert(defaultOptions);

      if (insertError) throw insertError;
    }
  };

  // Validate current step before proceeding
  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1: {
        const { fullName, phone, role } = wizardData.userInfo;
        if (validateRequired(fullName, 'שם מלא')) return 'נא להזין שם מלא';
        if (validatePhone(phone)) return validatePhone(phone);
        if (validateRequired(role, 'תפקיד')) return 'נא לבחור תפקיד';
        return null;
      }
      case 2: {
        const { address, constructionTypeId, startDate, projectPhaseId, totalBudget } =
          wizardData.projectInfo;
        if (validateRequired(address, 'כתובת')) return 'נא להזין כתובת';
        if (validateRequired(constructionTypeId, 'סוג בנייה')) return 'נא לבחור סוג בנייה';
        if (validateRequired(startDate, 'תאריך')) return 'נא לבחור תאריך התחלה';
        if (validateRequired(projectPhaseId, 'סטטוס')) return 'נא לבחור סטטוס פרויקט';
        if (totalBudget && isNaN(Number(totalBudget))) return 'תקציב חייב להיות מספר';
        return null;
      }
      case 3: {
        const floors = wizardData.floors?.floors || [];
        if (floors.length === 0) return 'נא להגדיר לפחות קומה אחת';
        return null;
      }
      // Steps 4-7 have no required validation
      default:
        return null;
    }
  };

  // Handle continue/save
  const handleContinue = async () => {
    // Validate before proceeding
    const validationError = validateCurrentStep();
    if (validationError) {
      setSaveError(validationError);
      return;
    }

    setIsLoading(true);
    setSaveError(null);

    try {
      // Save data based on current step
      switch (currentStep) {
        case 1:
          await saveUserInfo();
          break;
        case 2:
          await saveProjectInfo();
          break;
        case 3:
          await saveFloors();
          break;
        case 4:
          // FloorPlan step is optional - spaces are created via AI analysis
          // No explicit save needed here
          break;
        case 5:
          // Spaces are saved incrementally in SpacesStep
          break;
        case 6:
          await saveHomeChoices();
          break;
        case 7:
          // Auto-create construction items (ארון חשמל, ארון תקשורת) before completing
          try {
            await autoCreateConstructionItems(supabase, projectId);
          } catch (err) {
            console.error('Failed to auto-create construction items:', err);
          }

          // Mark onboarding as completed and navigate to overview
          await supabase
            .from(TABLES.PROJECT_INFO)
            .update({ onboarding_completed: true })
            .eq('id', projectId);

          // Update AppContext so AuthenticatedLayout doesn't redirect back
          setCurrentProject(prev => ({ ...prev, onboarding_completed: true }));

          navigate('/overview');
          return;
        default:
          break;
      }

      // Move to next step
      if (currentStep < 7) {
        setCurrentStep(prev => prev + 1);
      }
    } catch (err) {
      logError(err, {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA,
        message: `Failed to save wizard step ${currentStep}`,
        metadata: { step: currentStep, projectId },
      });
      setSaveError(err.message || 'שגיאה בשמירת הנתונים');
    } finally {
      setIsLoading(false);
    }
  };

  // Render current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <UserInfoStep data={wizardData.userInfo} onChange={handleUserInfoChange} />;
      case 2:
        return <ProjectInfoStep data={wizardData.projectInfo} onChange={handleProjectInfoChange} />;
      case 3:
        return <FloorsStep data={wizardData.floors} onChange={handleFloorsChange} />;
      case 4:
        return (
          <FloorPlanStep
            data={wizardData.floorPlan}
            onChange={handleFloorPlanChange}
            floorsData={wizardData.floors}
            projectId={projectId}
            savedFloorIds={savedFloorIds}
            onExtractedSpaces={handleExtractedSpaces}
            onSkip={() => setCurrentStep(5)}
          />
        );
      case 5:
        return (
          <SpacesStep
            data={wizardData.spaces}
            onChange={handleSpacesChange}
            floorsData={wizardData.floors}
            spaceTypes={spaceTypes}
            projectId={projectId}
            savedFloorIds={savedFloorIds}
            extractedSpaces={extractedSpaces}
          />
        );
      case 6:
        return (
          <HomeChoicesStep
            data={wizardData.homeChoices}
            onChange={handleHomeChoicesChange}
            homeChoiceItems={homeChoiceItems || []}
            floorsData={wizardData.floors}
            bundleChildItems={bundleChildItems || []}
          />
        );
      case 7:
        return <SummaryStep wizardData={wizardData} />;
      default:
        return <div>Step {currentStep}</div>;
    }
  };

  // Allow clicking on progress circles to jump to completed steps only
  const handleStepClick = stepId => {
    if (stepId < currentStep) {
      setCurrentStep(stepId);
    }
  };

  // Show welcome screen (step 0) before the wizard
  if (currentStep === 0 && !isInitializing) {
    return <WelcomeStep onStart={() => setCurrentStep(1)} />;
  }

  // Show loading while initializing existing data
  if (isInitializing) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: 'linear-gradient(135deg, #f0f4f8 0%, #e8eef5 100%)',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div
            className="auth-loading-spinner"
            style={{
              width: '40px',
              height: '40px',
              border: '3px solid #e2e8f0',
              borderTopColor: '#2563eb',
              borderRadius: '50%',
              animation: 'auth-spin 0.8s linear infinite',
              margin: '0 auto',
            }}
          />
          <p style={{ marginTop: '20px', color: '#64748b' }}>טוען...</p>
        </div>
      </div>
    );
  }

  return (
    <WizardLayout
      currentStep={currentStep}
      totalSteps={7}
      onBack={handleBack}
      onContinue={handleContinue}
      onStepClick={handleStepClick}
      isBackDisabled={currentStep === 1}
      isLoading={isLoading}
      continueLabel={currentStep === 7 ? 'סיים הגדרה' : 'המשך'}
      error={saveError}
      userName={wizardData.userInfo?.fullName || null}
      projectAddress={wizardData.projectInfo?.address || null}
    >
      {renderStepContent()}
    </WizardLayout>
  );
};

export default OnboardingWizard;
